import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { 
  Wallet, Trophy, Swords, Clock, Plus, ArrowUpRight, ArrowDownRight,
  TrendingUp, Gamepad2, ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import TermsModal from '../components/TermsModal';
import DashboardLayout from '../components/DashboardLayout';
import { AnnouncementPanel } from '../components/AnnouncementDisplay';
import WelcomePopup from '../components/WelcomePopup';
import WalletBreakdown from '../components/WalletBreakdown';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [matches, setMatches] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTerms, setShowTerms] = useState(false);
  const [showWelcomePopup, setShowWelcomePopup] = useState(false);

  useEffect(() => {
    if (user && !user.is_verified) {
      setShowTerms(true);
    }
    fetchData();
    checkWelcomeStatus();
  }, [user]);

  const fetchData = async () => {
    try {
      const [matchesRes, txnRes] = await Promise.all([
        axios.get(`${API}/matches/my-matches`, { withCredentials: true }),
        axios.get(`${API}/wallet/transactions`, { withCredentials: true })
      ]);
      setMatches(matchesRes.data);
      setTransactions((txnRes.data.transactions || txnRes.data || []).slice(0, 5));
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkWelcomeStatus = async () => {
    try {
      const res = await axios.get(`${API}/auth/welcome-status`, { withCredentials: true });
      if (res.data.should_show_popup) {
        // Delay popup slightly to let dashboard load first
        setTimeout(() => setShowWelcomePopup(true), 500);
      }
    } catch (error) {
      console.error('Error checking welcome status:', error);
    }
  };

  const handleTermsAccepted = () => {
    setShowTerms(false);
    refreshUser();
  };

  const activeMatches = matches.filter(m => 
    ['open', 'in_progress', 'awaiting_proof'].includes(m.status)
  );
  const completedMatches = matches.filter(m => m.status === 'completed');
  const wonMatches = completedMatches.filter(m => m.winner_id === user?.user_id);

  const stats = [
    { 
      label: 'Balance', 
      value: `$${((user?.credits_balance || 0) / 100).toFixed(2)}`, 
      icon: Wallet, 
      color: '#00F0FF',
      link: '/wallet' 
    },
    { 
      label: 'Winnings', 
      value: `$${(user?.cash_balance || 0).toFixed(2)}`, 
      icon: Wallet, 
      color: '#CCFF00',
      link: '/wallet' 
    },
    { 
      label: 'Active Matches', 
      value: activeMatches.length.toString(), 
      icon: Swords, 
      color: '#7000FF',
      link: '/lobby' 
    },
    { 
      label: 'Wins', 
      value: (user?.wins || 0).toString(), 
      icon: Trophy, 
      color: '#00FF94',
      link: '/history' 
    },
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'text-[#00F0FF] bg-[#00F0FF]/10';
      case 'in_progress': return 'text-[#FFB800] bg-[#FFB800]/10';
      case 'awaiting_proof': return 'text-[#7000FF] bg-[#7000FF]/10';
      case 'completed': return 'text-[#00FF94] bg-[#00FF94]/10';
      case 'disputed': return 'text-[#FF2E2E] bg-[#FF2E2E]/10';
      default: return 'text-white/50 bg-white/10';
    }
  };

  return (
    <DashboardLayout>
      {/* Terms Modal */}
      {showTerms && <TermsModal onAccept={handleTermsAccepted} />}

      <div className="max-w-7xl mx-auto" data-testid="dashboard-page">
        {/* Welcome */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="font-heading text-3xl font-bold mb-2">
            Welcome back, <span className="text-[#00F0FF]">{user?.name?.split(' ')[0]}</span>
          </h1>
          <p className="text-white/50">Ready to compete?</p>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {stats.map((stat, i) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              onClick={() => navigate(stat.link)}
              className="glass-card rounded-xl p-5 cursor-pointer hover:border-white/20 transition-all group"
              data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div 
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${stat.color}20` }}
                >
                  <stat.icon className="w-5 h-5" style={{ color: stat.color }} />
                </div>
                <ChevronRight className="w-4 h-4 text-white/30 group-hover:text-white/60 transition-colors" />
              </div>
              <p className="text-white/50 text-sm mb-1">{stat.label}</p>
              <p 
                className="font-mono text-2xl font-bold"
                style={{ color: stat.color, textShadow: `0 0 20px ${stat.color}50` }}
              >
                {stat.value}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Content Grid */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Active Matches */}
          <div className="lg:col-span-2">
            <div className="glass-card rounded-xl p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading font-semibold text-lg">Active Matches</h2>
                <Link to="/lobby" className="text-[#00F0FF] text-sm hover:underline flex items-center gap-1">
                  View All <ChevronRight className="w-4 h-4" />
                </Link>
              </div>

              {loading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="h-20 bg-white/5 rounded-lg animate-pulse" />
                  ))}
                </div>
              ) : activeMatches.length === 0 ? (
                <div className="text-center py-12">
                  <Swords className="w-12 h-12 text-white/20 mx-auto mb-4" />
                  <p className="text-white/50 mb-4">No active matches</p>
                  <Button 
                    onClick={() => navigate('/create-match')}
                    className="btn-primary clip-slant-sm"
                    data-testid="empty-create-match-btn"
                  >
                    Create Your First Match
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {activeMatches.slice(0, 5).map(match => (
                    <motion.div
                      key={match.match_id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      onClick={() => navigate(`/match/${match.match_id}`)}
                      className="flex items-center justify-between p-4 rounded-lg bg-white/5 hover:bg-white/10 cursor-pointer transition-colors"
                      data-testid={`match-${match.match_id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-[#7000FF]/20 flex items-center justify-center">
                          <Gamepad2 className="w-5 h-5 text-[#7000FF]" />
                        </div>
                        <div>
                          <p className="font-semibold">{match.game_name}</p>
                          <p className="text-sm text-white/50">
                            vs {match.opponent_name || 'Waiting for opponent'}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-mono font-bold text-[#CCFF00]">
                          {(match.wager_amount * 100).toLocaleString()} credits
                        </p>
                        <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(match.status)}`}>
                          {match.status.replace('_', ' ')}
                        </span>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar Column */}
          <div className="space-y-6">
            {/* Wallet Overview (Compact) */}
            <WalletBreakdown compact={true} />
            
            {/* Announcements Panel */}
            <AnnouncementPanel />
            
            {/* Recent Transactions */}
            <div className="glass-card rounded-xl p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading font-semibold text-lg">Recent Activity</h2>
                <Link to="/wallet" className="text-[#00F0FF] text-sm hover:underline flex items-center gap-1">
                  View All <ChevronRight className="w-4 h-4" />
                </Link>
              </div>

              {loading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="h-14 bg-white/5 rounded-lg animate-pulse" />
                  ))}
                </div>
              ) : transactions.length === 0 ? (
                <div className="text-center py-8">
                  <Clock className="w-10 h-10 text-white/20 mx-auto mb-3" />
                  <p className="text-white/50 text-sm">No recent activity</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {transactions.map(txn => (
                    <div 
                      key={txn.transaction_id}
                      className="flex items-center justify-between py-3 border-b border-white/5 last:border-0"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          txn.amount > 0 ? 'bg-[#00FF94]/20' : 'bg-[#FF2E2E]/20'
                        }`}>
                          {txn.amount > 0 ? (
                            <ArrowDownRight className="w-4 h-4 text-[#00FF94]" />
                          ) : (
                            <ArrowUpRight className="w-4 h-4 text-[#FF2E2E]" />
                          )}
                        </div>
                        <div>
                          <p className="text-sm font-medium">{txn.type.replace('_', ' ')}</p>
                          <p className="text-xs text-white/40 truncate max-w-[120px]">{txn.description}</p>
                        </div>
                      </div>
                      <p className={`font-mono font-bold ${
                        txn.amount > 0 ? 'text-[#00FF94]' : 'text-[#FF2E2E]'
                      }`}>
                        {txn.amount > 0 ? '+' : ''}{txn.amount.toFixed(2)}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Quick Actions */}
            <div className="glass-card rounded-xl p-6 mt-6">
              <h2 className="font-heading font-semibold text-lg mb-4">Quick Actions</h2>
              <div className="space-y-3">
                <Button 
                  onClick={() => navigate('/wallet')}
                  variant="outline"
                  className="w-full btn-secondary justify-start"
                  data-testid="quick-purchase-credits-btn"
                >
                  <Wallet className="w-4 h-4 mr-2 text-[#00F0FF]" />
                  Purchase Credits
                </Button>
                <Button 
                  onClick={() => navigate('/lobby')}
                  variant="outline"
                  className="w-full btn-secondary justify-start"
                  data-testid="quick-lobby-btn"
                >
                  <Swords className="w-4 h-4 mr-2 text-[#00F0FF]" />
                  Browse Matches
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Welcome Popup Modal */}
      {showWelcomePopup && (
        <WelcomePopup onClose={() => setShowWelcomePopup(false)} />
      )}
    </DashboardLayout>
  );
};

export default Dashboard;
